// Modal centrada "Subir varios productos" (diseño ATLAS). Dos fuentes, dos estilos:
//   • Archivos LOCALES (drag & drop / "Subir archivos") → lista con nombre editable
//     + tachito (igual que siempre). Lógica en el hook `useBulkUpload`.
//   • TIENDANUBE → grid de cuadrados SELECCIONABLES (sin editar nombre ni borrar):
//     elegís cuáles traer + "Seleccionar todos". Por defecto NINGUNO seleccionado.
// Al confirmar, crea los locales (hook) + los de Tiendanube ELEGIDOS (webhook n8n).

import React from "react";
import { createPortal } from "react-dom";
import {
  X,
  UploadCloud,
  Layers,
  Pencil,
  Check,
  Trash2,
  Loader2,
  Package,
  Info,
} from "lucide-react";
import { useBulkUpload, type BulkItem } from "../hooks/useBulkUpload";
import { useExitTransition } from "../hooks/useExitTransition";
import type { FileGroup } from "../utils/groupFilesByStem";
import { supabase } from "../lib/supabase";
import { ensureTnProducts, fireTnImport, type TnProduct } from "../lib/tiendaNube";
import { TiendaNubeIcon } from "./TiendaNubeIcon";
import { TiendaNubeConnectModal } from "./TiendaNubeConnectModal";

// Tope de productos de Tiendanube por import (acordado: ~50). El front los manda
// en tandas de TN_BATCH al webhook → el back los procesa con un worker por
// producto. Con el dedup del Paso 1, se importa en olas sin saturar.
const MAX_TN = 50;
const TN_BATCH = 25;

// Duración de la animación de entrada/salida. Tiene que coincidir con las
// `duration-*` de las clases de abajo: `useExitTransition` desmonta a los
// EXIT_MS, y si fuera menor que la transition la salida se cortaría a la
// mitad. 300 ms — igual que el resto de los sheets de la app.
const EXIT_MS = 300;

interface Props {
  open: boolean;
  companyId: string | null;
  brandId: string | null;
  userPlan: string;
  onClose: () => void;
  /** Refresca la grilla de productos cuando se crean. */
  onSaved: () => Promise<void> | void;
  /** Altura de apilado del overlay. Default 80 (Mi Marca, donde no compite
   *  con nada). La Interfaz V2 lo abre POR ENCIMA de sus paneles, que viven
   *  entre z-100 (PresetDetailSheet) y z-211 (MultiSlotProductPickerSheet):
   *  sin esto el popup queda tapado por el panel que lo abrió (ADR-684).
   *  Va como estilo inline y no como clase `z-[N]`: Tailwind no compila
   *  clases armadas en runtime. */
  zIndex?: number;
}

export const AgregarVariosModal: React.FC<Props> = ({
  open,
  companyId,
  brandId,
  userPlan,
  onClose,
  onSaved,
  zIndex = 80,
}) => {
  const bulk = useBulkUpload({
    idCompany: companyId,
    userPlan,
    brandId,
    onSuccess: onSaved,
  });
  const [dragOver, setDragOver] = React.useState(false);
  // Entrada Y salida animadas. `useExitTransition` mantiene el componente
  // montado durante EXIT_MS después de que `open` pasa a false, para que la
  // CSS transition de salida alcance a correr antes del unmount (ADR-686).
  const { mounted, animated } = useExitTransition(open, EXIT_MS);
  // El estado NO se limpia al cerrar sino EXIT_MS después: si se limpiara al
  // toque, durante la salida se vería el sheet ya vacío (la lista de productos
  // desapareciendo antes que el sheet). Si el user reabre antes de que el
  // timer corra, lo cancelamos — si no, borraría la tanda nueva.
  const resetTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetAfterExit = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      resetTimerRef.current = null;
      bulk.clearAll();
      setTnItems([]);
      setTnSelected(new Set());
      setError(null);
      setNotice(null);
    }, EXIT_MS);
  };
  React.useEffect(() => {
    if (!open || !resetTimerRef.current) return;
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = null;
  }, [open]);
  React.useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    [],
  );
  // Mobile = bottom sheet (entra deslizando desde abajo); desktop = modal
  // centrada. Es por VIEWPORT, no por puntero: el layout tiene que seguir al
  // ancho de la ventana, así que reacciona al resize (ADR-685).
  const [isMobile, setIsMobile] = React.useState(
    () =>
      typeof window !== "undefined" &&
      !window.matchMedia("(min-width: 768px)").matches,
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsMobile(!mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // El drag&drop de archivos no sirve en mobile/PWA (no se pueden arrastrar
  // archivos del SO) → ocultamos esa zona ahí y dejamos solo los botones.
  const [pointerSupportsDrop] = React.useState(
    () =>
      typeof window !== "undefined" &&
      !window.matchMedia("(pointer: coarse)").matches &&
      !window.matchMedia("(display-mode: standalone)").matches,
  );
  // La zona de arrastre se ata TAMBIÉN al viewport: en formato bottom sheet no
  // tiene sentido ofrecer "arrastrá tus imágenes acá" aunque el dispositivo
  // técnicamente tenga mouse (ventana de escritorio angosta). Un solo criterio
  // para la presentación y para el drop.
  const dropEnabled = pointerSupportsDrop && !isMobile;
  const [tnItems, setTnItems] = React.useState<TnProduct[]>([]);
  const [tnSelected, setTnSelected] = React.useState<Set<number>>(new Set());
  const [tnLoading, setTnLoading] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  // Aviso "vas a conectar otra tienda" (solo si la empresa ya tiene una).
  const [warnOpen, setWarnOpen] = React.useState(false);
  const warnResolveRef = React.useRef<((v: boolean) => void) | null>(null);
  // Drag-to-select de los productos de Tiendanube: mantener apretado + arrastrar
  // selecciona varios. Sin "modo" (la grilla ya es siempre seleccionable).
  const longPressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const longPressTriggeredRef = React.useRef(false);
  const dragSelectRef = React.useRef<{
    active: boolean;
    toggleTo: "select" | "deselect";
    visited: Set<number>;
    startX: number;
    startY: number;
    hasMoved: boolean;
  } | null>(null);

  // Listener global del arrastre: detecta las cards bajo el dedo/mouse
  // (`elementFromPoint` + `[data-tn-id]`) y las selecciona. Threshold para no
  // confundir con scroll; preventDefault para frenar el scroll mientras arrastra.
  React.useEffect(() => {
    if (!open) return;
    const applyToCard = (id: number, toggleTo: "select" | "deselect") =>
      setTnSelected((prev) => {
        const next = new Set(prev);
        if (toggleTo === "select") {
          if (next.has(id) || next.size >= MAX_TN) return prev;
          next.add(id);
        } else {
          if (!next.has(id)) return prev;
          next.delete(id);
        }
        return next;
      });
    const cardIdAt = (x: number, y: number): number | null => {
      const card = document
        .elementFromPoint(x, y)
        ?.closest("[data-tn-id]");
      const attr = card?.getAttribute("data-tn-id");
      return attr ? Number(attr) : null;
    };
    const move = (
      x: number,
      y: number,
      threshold: number,
      prevent: () => void,
    ) => {
      const st = dragSelectRef.current;
      if (!st) return;
      if (!st.active && Math.hypot(x - st.startX, y - st.startY) > threshold) {
        st.active = true;
        st.hasMoved = true;
        const first = st.visited.values().next().value as number | undefined;
        if (first != null) applyToCard(first, st.toggleTo);
      }
      if (st.active) {
        prevent();
        const id = cardIdAt(x, y);
        if (id != null && !st.visited.has(id)) {
          st.visited.add(id);
          applyToCard(id, st.toggleTo);
        }
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t)
        move(t.clientX, t.clientY, 18, () => {
          if (e.cancelable) e.preventDefault();
        });
    };
    const onMouseMove = (e: MouseEvent) =>
      move(e.clientX, e.clientY, 8, () => e.preventDefault());
    const onEnd = () => {
      const st = dragSelectRef.current;
      if (st?.hasMoved) longPressTriggeredRef.current = true; // suprime el click
      dragSelectRef.current = null;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onEnd);
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onEnd);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, [open]);

  if (!mounted) return null;

  const busy = creating || bulk.saving || tnLoading;
  const localCount = bulk.useFileNames ? bulk.groups.length : bulk.items.length;
  const total = localCount + tnSelected.size;
  const creatable = localCount > 0 || tnSelected.size > 0;
  // "Seleccionar todos" selecciona hasta el tope (no más de MAX_TN).
  const selectableTn = Math.min(tnItems.length, MAX_TN);
  const allSelected = selectableTn > 0 && tnSelected.size === selectableTn;

  const handleClose = () => {
    if (busy) return;
    onClose();
    resetAfterExit();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) {
      // Acción nueva → el error local viejo (guardado/TN) deja de aplicar;
      // sin esto taparía el bulk.error del archivo recién salteado.
      setError(null);
      void bulk.addFiles(e.dataTransfer.files);
    }
  };

  const handleTraerTiendaNube = async () => {
    if (busy) return;
    setError(null);
    setNotice(null);
    setTnLoading(true);
    try {
      const { products } = await ensureTnProducts(brandId, {
        confirmNewStore: () =>
          new Promise<boolean>((resolve) => {
            warnResolveRef.current = resolve;
            setWarnOpen(true);
          }),
      });
      if (products.length === 0) {
        setNotice("No hay productos nuevos con foto para traer de tu tienda.");
        return;
      }
      // Sumar a la grilla, sin duplicar por id (los nuevos vienen sin seleccionar).
      setTnItems((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...products.filter((p) => !seen.has(p.id))];
      });
    } catch (e) {
      if (e instanceof Error && e.message === "CANCELLED") return; // canceló el aviso
      setError(e instanceof Error ? e.message : "No se pudo traer la tienda.");
    } finally {
      setTnLoading(false);
    }
  };

  const toggleTn = (id: number) =>
    setTnSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_TN) next.add(id); // no pasar del tope
      return next;
    });

  const toggleAll = () =>
    setTnSelected(
      allSelected
        ? new Set()
        : new Set(tnItems.slice(0, MAX_TN).map((t) => t.id)), // hasta el tope
    );

  // Inicio del gesto sobre una card. Mouse: trackea ya (arrastrar = seleccionar,
  // click = toggle). Touch: arma tras "mantener apretado" 350ms (para NO romper
  // el scroll de la grilla). El movimiento lo maneja el listener global.
  const startTnDrag = (id: number, x: number, y: number, isTouch: boolean) => {
    const toggleTo: "select" | "deselect" = tnSelected.has(id)
      ? "deselect"
      : "select";
    const arm = () => {
      dragSelectRef.current = {
        active: false,
        toggleTo,
        visited: new Set([id]),
        startX: x,
        startY: y,
        hasMoved: false,
      };
    };
    if (isTouch) {
      longPressTriggeredRef.current = false;
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = setTimeout(() => {
        arm();
        if (navigator.vibrate) navigator.vibrate(30); // feedback "ya podés arrastrar"
      }, 350);
    } else {
      arm();
    }
  };
  // Si el dedo se mueve antes de armar (scroll), cancelamos el "mantener apretado".
  const cancelTnLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  // Click de una card: si vino de un arrastre lo ignoramos; si no, toggle normal.
  const tnCardClick = (id: number) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (!busy) toggleTn(id);
  };

  const handleCreate = async () => {
    if (busy || !creatable) return;
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      let localOk = true;
      if (bulk.items.length > 0) {
        const r = await bulk.saveAll();
        localOk = r === "all-success" || r === "none";
      }
      const selected = tnItems.filter((t) => tnSelected.has(t.id));
      if (selected.length > 0) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const productos = selected.map((t) => ({
          nombre: t.nombre,
          imgs: t.imgs,
          tn_product_id: t.id,
          variantes: t.variantes,
        }));
        // Mandar de a TN_BATCH (25): el back procesa por tandas (worker/queue).
        for (let i = 0; i < productos.length; i += TN_BATCH) {
          await fireTnImport({
            userId: user?.id,
            companyId,
            brandId,
            productos: productos.slice(i, i + TN_BATCH),
          });
        }
        await new Promise((r) => setTimeout(r, 3500));
        await onSaved();
      }
      if (localOk) {
        // Mismo criterio que handleClose: cerrar primero y limpiar después de
        // la animación, para no ver la lista vaciarse mientras el sheet sale.
        onClose();
        resetAfterExit();
      } else {
        setError(
          bulk.error || "Algunos productos fallaron — revisalos e intentá de nuevo.",
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hubo un error.");
    } finally {
      setCreating(false);
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 bg-black/55 flex justify-center transition-opacity duration-300 ease-out ${
        isMobile ? "items-end" : "items-center p-4"
      } ${animated ? "opacity-100" : "opacity-0"}`}
      style={{ zIndex }}
    >
      <div
        className={
          isMobile
            ? // Bottom sheet: pegado abajo, esquinas superiores redondeadas,
              // entra y sale deslizando (translateY 100% ↔ 0). El alto lo
              // limita el viewport menos el notch, así el header siempre queda
              // alcanzable.
              `bg-white rounded-t-[13px] shadow-[0_-8px_40px_rgba(0,0,0,0.18)] w-full max-h-[calc(100dvh-env(safe-area-inset-top,0px)-1rem)] flex flex-col overflow-hidden transition-transform duration-300 ease-out ${
                animated ? "translate-y-0" : "translate-y-full"
              }`
            : // Desktop: fade + un escalado mínimo. Sutil a propósito — una
              // modal centrada que se desliza se siente pesada.
              `bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.18)] w-[min(640px,calc(100vw-32px))] max-h-[88vh] flex flex-col overflow-hidden transition-all duration-300 ease-out ${
                animated ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`
        }
      >
        {/* Pill de arrastre — señal visual de "esto es un sheet". Solo mobile. */}
        {isMobile && (
          <div className="py-2 shrink-0">
            <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
          </div>
        )}
        {/* Header */}
        <div
          className={`px-6 pb-2 flex items-start justify-between gap-3 ${
            isMobile ? "pt-1" : "pt-6"
          }`}
        >
          <div className="min-w-0">
            <h2 className="text-[20px] font-semibold text-brand-black/90">
              Subir varios productos
            </h2>
            <p className="text-[13px] text-brand-black/50 mt-1">
              {dropEnabled
                ? "Arrastrá imágenes, subí archivos o traé tu Tiendanube. Cada imagen es un producto distinto."
                : "Subí archivos o traé tu Tiendanube. Cada imagen es un producto distinto."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            aria-label="Cerrar"
            className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Input oculto del hook (file picker) */}
        <input
          ref={bulk.fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            // Igual que en onDrop: acción nueva limpia el error local viejo,
            // para que un bulk.error fresco no quede tapado por él.
            setError(null);
            bulk.handleSelectFiles(e);
          }}
        />

        <div className="flex-1 overflow-y-auto px-6 pb-2">
          {/* Zona drag & drop + acciones */}
          <div
            onDragOver={
              dropEnabled
                ? (e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }
                : undefined
            }
            onDragLeave={dropEnabled ? () => setDragOver(false) : undefined}
            onDrop={dropEnabled ? onDrop : undefined}
            className={
              dropEnabled
                ? `rounded-xl border-2 border-dashed px-6 py-7 text-center transition-colors ${
                    dragOver
                      ? "border-brand-primary bg-brand-primary/5"
                      : "border-neutral-200"
                  }`
                : "text-center"
            }
          >
            {dropEnabled && (
              <>
                <UploadCloud className="w-7 h-7 mx-auto text-brand-black/30 mb-2" />
                <p className="text-[14px] text-brand-black/60">
                  Arrastrá tus imágenes acá
                </p>
                <p className="text-[12px] text-brand-black/35 my-1">o</p>
              </>
            )}
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-1">
              <button
                type="button"
                onClick={bulk.openFilePicker}
                disabled={busy || bulk.convertingHeic}
                className="inline-flex justify-center items-center gap-2 h-[var(--btn-h)] px-4 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[length:var(--text-body)] font-medium shadow-sm transition-colors disabled:opacity-60"
              >
                <Layers className="w-4 h-4" />
                Subir archivos
              </button>
              <button
                type="button"
                onClick={handleTraerTiendaNube}
                disabled={busy}
                className="inline-flex justify-center items-center gap-2 h-[var(--btn-h)] px-4 rounded-md border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100 text-[length:var(--text-body)] font-medium shadow-sm transition-colors disabled:opacity-60"
              >
                {tnLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <TiendaNubeIcon className="h-4 w-auto text-[#2c3357]" />
                )}
                {tnLoading ? "Conectando…" : "Traer de Tiendanube"}
              </button>
            </div>
            {bulk.convertingHeic && (
              <p className="text-[12px] text-brand-black/50 mt-3 inline-flex items-center gap-1.5 justify-center">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Procesando imágenes…
              </p>
            )}
          </div>

          {/* ── Archivos locales (lista con nombre editable) ── */}
          {bulk.items.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => bulk.setUseFileNames(!bulk.useFileNames)}
                className="mt-3 w-full flex items-center justify-between rounded-xl border border-neutral-200 px-4 py-2.5"
              >
                <span className="text-[13.5px] text-brand-black/70">
                  Usar nombres de archivo
                </span>
                <span
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${bulk.useFileNames ? "bg-brand-primary" : "bg-neutral-300"}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${bulk.useFileNames ? "translate-x-4" : "translate-x-0.5"}`}
                  />
                </span>
              </button>

              <div className="mt-3 space-y-2">
                {bulk.useFileNames
                  ? bulk.groups.map((g) => (
                      <GroupRow
                        key={g.key}
                        group={g}
                        displayName={bulk.groupDisplayName(g)}
                        onRename={(name) => bulk.updateGroupName(g.key, name)}
                        onRemove={() => bulk.removeGroup(g.key)}
                        disabled={busy}
                      />
                    ))
                  : bulk.items.map((it, idx) => (
                      <ItemRow
                        key={it.id}
                        item={it}
                        placeholder={bulk.defaultNameFor(idx)}
                        onRename={(name) => bulk.updateName(it.id, name)}
                        onRemove={() => bulk.removeItem(it.id)}
                        disabled={busy}
                      />
                    ))}
              </div>
            </>
          )}

          {/* ── Tiendanube (grid cuadrado seleccionable) ── */}
          {tnItems.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12.5px] font-medium text-brand-black/55">
                  De Tiendanube · {tnSelected.size} de {selectableTn}{" "}
                  seleccionado{tnSelected.size === 1 ? "" : "s"}
                  {tnItems.length > MAX_TN && (
                    <span className="text-brand-black/40">
                      {" "}
                      · máx {MAX_TN} por vez
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={toggleAll}
                  disabled={busy}
                  className="text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-opacity disabled:opacity-40"
                >
                  {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {tnItems.map((t) => (
                  <TnGridCard
                    key={t.id}
                    item={t}
                    selected={tnSelected.has(t.id)}
                    disabled={busy}
                    onCardClick={() => tnCardClick(t.id)}
                    onPointerStart={(x, y, isTouch) =>
                      startTnDrag(t.id, x, y, isTouch)
                    }
                    onPointerCancel={cancelTnLongPress}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {notice && (
          <p className="px-6 pb-1 text-[13px] text-neutral-500">{notice}</p>
        )}
        {error && <p className="px-6 pb-1 text-[13px] text-error">{error}</p>}
        {/* Errores del hook (archivo no decodificable, tamaño excedido, formato
            bloqueado): sin esto, un archivo salteado por addFiles desaparecía
            de la tanda sin explicación — el modal solo mostraba su error local. */}
        {!error && bulk.error && (
          <p className="px-6 pb-1 text-[13px] text-error">{bulk.error}</p>
        )}
        {bulk.saving && (
          <p className="px-6 pb-1 text-[13px] text-brand-black/55">
            Creando {bulk.progress.done}/{bulk.progress.total}…
          </p>
        )}

        {/* Aviso de demora al traer de Tiendanube (la importación corre en
            background en n8n: descarga cada foto de la tienda → puede tardar
            unos minutos). Solo si hay productos de TN seleccionados. */}
        {tnSelected.size > 0 && (
          <div className="mx-6 mb-2 flex items-start gap-2 rounded-lg border border-brand-primary/15 bg-brand-primary/[0.06] px-3 py-2">
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-brand-primary" />
            <p className="text-[12.5px] leading-snug text-brand-black/65">
              {creating
                ? "Estamos trayendo tus productos de Tiendanube. Puede tardar unos minutos — se van sumando solos a tu catálogo."
                : "Traer productos de Tiendanube puede tardar unos minutos. Se van sumando solos a tu catálogo; podés cerrar esta ventana y seguir usando la app."}
            </p>
          </div>
        )}

        {/* Footer. En sheet, el padding de abajo suma el safe-area para que los
            botones no queden bajo la barra de gestos del teléfono. */}
        <div
          className="px-6 py-4 flex items-center justify-end gap-3 border-t border-neutral-100 shrink-0"
          style={
            isMobile
              ? { paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }
              : undefined
          }
        >
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="h-[44px] px-3 text-[14px] font-medium text-brand-black/55 hover:text-brand-black/90 transition-colors disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!creatable || busy}
            className="inline-flex items-center justify-center gap-2 h-[44px] px-6 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            {creating
              ? "Creando…"
              : total > 0
                ? `Crear ${total} producto${total === 1 ? "" : "s"}`
                : "Crear productos"}
          </button>
        </div>
      </div>

      {/* Aviso al conectar — MISMO modal (compartido) que el botón "Conectar" de
          Mi marca, pero SIN la explicación (acá ya estás importando) → solo la nota
          multi-tienda + el link de logout. Solo se dispara si la empresa ya tiene
          otra tienda (lo decide `confirmNewStore` con company_has_stores). */}
      <TiendaNubeConnectModal
        open={warnOpen}
        showExplanation={false}
        companyHasStores
        onCancel={() => {
          warnResolveRef.current?.(false);
          setWarnOpen(false);
        }}
        onConnect={() => {
          warnResolveRef.current?.(true);
          setWarnOpen(false);
        }}
        zIndex={zIndex + 10}
      />
    </div>,
    document.body,
  );
};

// ── Tarjeta cuadrada seleccionable (Tiendanube) ──
// Mapa best-effort de nombre de color (es) → hex, para los circulitos del grid.
// Lo que no matchea usa el gris neutro (el circulito igual comunica "hay colores").
const COLOR_HEX: Record<string, string> = {
  negro: "#111111", blanco: "#f5f5f5", gris: "#9ca3af", plata: "#c0c0c0",
  rojo: "#dc2626", bordo: "#7f1d1d", rosa: "#ec4899", naranja: "#f97316",
  amarillo: "#eab308", verde: "#16a34a", oliva: "#556b2f", celeste: "#38bdf8",
  azul: "#2563eb", marino: "#1e3a8a", violeta: "#7c3aed", lila: "#c4b5fd",
  marron: "#78350f", "marrón": "#78350f", beige: "#e7dcc8", crema: "#f3ead3",
  dorado: "#d4af37", camel: "#c19a6b",
};
const colorHex = (name: string): string =>
  COLOR_HEX[name.trim().toLowerCase()] ?? "#d1d5db";

const TnGridCard: React.FC<{
  item: TnProduct;
  selected: boolean;
  disabled?: boolean;
  onCardClick: () => void;
  onPointerStart: (clientX: number, clientY: number, isTouch: boolean) => void;
  onPointerCancel: () => void;
}> = ({
  item,
  selected,
  disabled,
  onCardClick,
  onPointerStart,
  onPointerCancel,
}) => (
  <button
    type="button"
    data-tn-id={item.id}
    draggable={false}
    onDragStart={(e) => e.preventDefault()}
    onClick={onCardClick}
    onMouseDown={(e) => {
      if (e.button === 0) onPointerStart(e.clientX, e.clientY, false);
    }}
    onTouchStart={(e) => {
      const t = e.touches[0];
      if (t) onPointerStart(t.clientX, t.clientY, true);
    }}
    onTouchMove={onPointerCancel}
    onTouchEnd={onPointerCancel}
    disabled={disabled}
    title={item.nombre}
    // Bloquea la selección/arrastre nativo de la imagen (copiar, menú "guardar
    // imagen" en mobile) para que no pelee con el drag-select.
    style={
      {
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      } as React.CSSProperties
    }
    className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-colors disabled:opacity-60 ${
      selected
        ? "border-brand-primary"
        : "border-transparent hover:border-neutral-200"
    }`}
  >
    {item.img_src ? (
      <img
        src={item.img_src}
        alt=""
        loading="lazy"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
      />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
        <Package className="w-5 h-5 text-brand-black/25" strokeWidth={1.5} />
      </div>
    )}
    {selected && (
      <div className="absolute inset-0 bg-brand-primary/15 pointer-events-none" />
    )}
    <span
      className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full inline-flex items-center justify-center pointer-events-none transition-colors ${
        selected
          ? "bg-brand-primary text-white"
          : "bg-white/85 border border-black/10 text-transparent"
      }`}
    >
      <Check className="w-3 h-3" />
    </span>
    {item.variantes && item.variantes.length > 1 && (
      <span className="absolute top-1.5 left-1.5 flex items-center gap-0.5 pointer-events-none">
        {(item.colores && item.colores.length > 0
          ? item.colores
          : item.variantes.map((v) => v.nombre)
        )
          .slice(0, 5)
          .map((c, i) => (
            <span
              key={i}
              className="w-2.5 h-2.5 rounded-full ring-1 ring-white/80 shadow-sm"
              style={{ backgroundColor: item.colores ? colorHex(c) : "#d1d5db" }}
            />
          ))}
      </span>
    )}
    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/65 to-transparent px-1.5 pt-4 pb-1 text-white text-[11px] leading-tight line-clamp-2 text-left pointer-events-none select-none">
      {item.nombre}
    </span>
  </button>
);

// ── Fila de un item local (1 imagen = 1 producto) ──
const ItemRow: React.FC<{
  item: BulkItem;
  placeholder: string;
  onRename: (name: string) => void;
  onRemove: () => void;
  disabled?: boolean;
}> = ({ item, placeholder, onRename, onRemove, disabled }) => {
  const [editing, setEditing] = React.useState(false);
  return (
    <div className="flex items-center gap-3 border border-neutral-200 rounded-xl p-2.5">
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-neutral-100 shrink-0">
        <img src={item.previewUrl} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={item.nombre}
            placeholder={placeholder}
            onChange={(e) => onRename(e.target.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setEditing(false);
            }}
            className="w-full px-2 py-1 rounded-md border border-neutral-200 text-[14px] text-brand-black/90 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none"
          />
        ) : (
          <span
            className={`block text-[14px] truncate ${item.nombre ? "text-brand-black/80" : "text-brand-black/40"}`}
          >
            {item.nombre || placeholder}
          </span>
        )}
      </div>
      <RowActions
        editing={editing}
        disabled={disabled}
        onEdit={() => setEditing((v) => !v)}
        onRemove={onRemove}
      />
    </div>
  );
};

// ── Fila de un grupo local (varias imágenes = 1 producto) ──
const GroupRow: React.FC<{
  group: FileGroup<BulkItem>;
  displayName: string;
  onRename: (name: string) => void;
  onRemove: () => void;
  disabled?: boolean;
}> = ({ group, displayName, onRename, onRemove, disabled }) => {
  const [editing, setEditing] = React.useState(false);
  const cover = group.files[0]?.previewUrl;
  return (
    <div className="flex items-center gap-3 border border-neutral-200 rounded-xl p-2.5">
      <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-neutral-100 shrink-0">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <Package className="w-5 h-5 text-brand-black/25 m-auto" />
        )}
        {group.files.length > 1 && (
          <span className="absolute bottom-0.5 right-0.5 text-[10px] font-medium bg-black/60 text-white rounded px-1">
            {group.files.length}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            defaultValue={displayName}
            onChange={(e) => onRename(e.target.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setEditing(false);
            }}
            className="w-full px-2 py-1 rounded-md border border-neutral-200 text-[14px] text-brand-black/90 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none"
          />
        ) : (
          <span className="block text-[14px] text-brand-black/80 truncate">
            {displayName}
          </span>
        )}
      </div>
      <RowActions
        editing={editing}
        disabled={disabled}
        onEdit={() => setEditing((v) => !v)}
        onRemove={onRemove}
      />
    </div>
  );
};

const RowActions: React.FC<{
  editing: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ editing, disabled, onEdit, onRemove }) => (
  <>
    <button
      type="button"
      onClick={onEdit}
      disabled={disabled}
      title="Editar nombre"
      className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md text-brand-black/40 hover:bg-neutral-100 hover:text-brand-black/70 transition-colors disabled:opacity-40"
    >
      {editing ? <Check className="w-4 h-4" /> : <Pencil className="w-3.5 h-3.5" />}
    </button>
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      title="Quitar"
      className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md text-brand-black/40 hover:bg-error-soft hover:text-error transition-colors disabled:opacity-40"
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </>
);

export default AgregarVariosModal;
